import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Car, Factory, Ship, FlaskConical } from "lucide-react";
import UnixxProducts from "@/components/UnixxProducts";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const CATEGORIES = [
  {
    icon: Car,
    name: "Automotive engine oils",
    items: ["SAE 15W-40", "20W-50", "5W-30 / 5W-40"],
    text: "API-graded passenger car and heavy-duty diesel engine oils, blended to OEM requirements.",
  },
  {
    icon: Factory,
    name: "Industrial oils",
    items: ["Hydraulic ISO VG 32–68", "Industrial gear oils", "Turbine & compressor oils"],
    text: "Fluids that keep plant machinery, gearboxes and circulating systems running between shutdowns.",
  },
  {
    icon: Ship,
    name: "Marine lubricants",
    items: ["Trunk piston engine oils", "Marine cylinder oils", "Stern tube oils"],
    text: "Formulations for coastal fleets and ocean-going vessels, available at regional ports.",
  },
  {
    icon: FlaskConical,
    name: "Base oils",
    items: ["SN150", "SN500", "BS150"],
    text: "Group I base oils for blenders; Group II grades quoted on request against demand.",
  },
];

const BASE_OIL_SPECS = [
  ["Kinematic viscosity @ 40°C, cSt", "28 – 33", "95 – 105", "470 – 510"],
  ["Viscosity index", "≥ 95", "≥ 95", "≥ 90"],
  ["Flash point (COC), °C", "≥ 200", "≥ 230", "≥ 280"],
  ["Pour point, °C", "≤ -6", "≤ -3", "≤ +3"],
];

const PACKAGING = ["208 L steel drums", "1,000 L IBC", "Flexitank & bulk parcels"];

export default function Lubricants() {
  return (
    <div data-testid="lubricants-page">
      <section className="relative overflow-hidden">
        <img src="/assets/lubricants.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/90 to-navy" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Product Line 02</motion.p>
          <motion.h1 {...fadeUp} className="mt-4 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">Lubricants</motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">
            Finished lubricants and base oils for automotive, industrial and marine applications — blended to
            specification and supplied in drums, IBCs or bulk.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12">
        <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">What we supply</motion.h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c, i) => (
            <motion.div
              key={c.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50"
              data-testid={`lubricant-card-${i}`}
            >
              <c.icon className="h-8 w-8 text-brand" />
              <h3 className="mt-5 text-lg font-bold text-white">{c.name}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.items.map((it) => (
                  <span key={it} className="rounded-full border border-navy-600 px-3 py-1 font-mono text-xs text-brand">{it}</span>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <UnixxProducts />

      <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="base-oil-spec-section">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">Group I base oil guide</motion.h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-navy-600" data-testid="base-oil-spec-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-navy-600 bg-navy-700 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-4 font-medium">Property</th>
                  <th className="px-6 py-4 font-medium text-brand">SN150</th>
                  <th className="px-6 py-4 font-medium text-brand">SN500</th>
                  <th className="px-6 py-4 font-medium text-brand">BS150</th>
                </tr>
              </thead>
              <tbody>
                {BASE_OIL_SPECS.map(([prop, ...vals]) => (
                  <tr key={prop} className="border-b border-navy-600/50 bg-navy-800 last:border-0">
                    <td className="px-6 py-4 text-slate-300">{prop}</td>
                    {vals.map((v) => (
                      <td key={v} className="px-6 py-4 font-mono text-slate-200">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Indicative values per ASTM methods. Certificates of Analysis are issued per cargo.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 md:px-12 lg:grid-cols-2">
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Packaging</p>
          <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">From a single drum to a full parcel</h2>
          <p className="mt-5 text-base leading-relaxed text-slate-300">
            Whether you stock a workshop or run a blending plant, we match pack size to your operation.
          </p>
          <ul className="mt-7 space-y-3">
            {PACKAGING.map((p, i) => (
              <li key={p} className="flex items-center gap-3 text-sm text-slate-300" data-testid={`lube-packaging-${i}`}>
                <span className="h-2 w-2 rounded-full bg-brand" /> {p}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="lubricants-cta-quote"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-7 py-3 text-sm font-semibold text-navy transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_28px_rgba(0,208,132,0.4)]"
            >
              Request lubricant pricing <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="/specs/base-oils-spec-sheet.pdf"
              download
              data-testid="lubricants-spec-download"
              className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-7 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
            >
              Download spec sheet (PDF)
            </a>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="float-slow overflow-hidden rounded-lg border border-navy-600">
          <img src="/assets/drums.webp" alt="Industrial lubricant drums" className="h-full w-full object-cover" />
        </motion.div>
      </section>
    </div>
  );
}
